// Document AI Summary — which documents the option applies to.
// Access (FR-14) is decided by transaction-service: the status list only loads
// for primary parties (the roles below, plus anyone added as primary on the
// transaction, e.g. a broker), and every request is checked again.

// accessControl.userRole values after normalizeRole(): co-agents map to
// LISTING_AGENT / BUYER_AGENT, so these five cover the always-primary roles
// (Listing Agent, Buyer's Agent, Co-Listing Agent, Co-Buyer's Agent, TC,
// Buyer, Seller). Used only to decide who sees the FR-4 error state when the
// status list can't be loaded.
const PRIMARY_PARTY_ROLES = new Set([
  'LISTING_AGENT',
  'BUYER_AGENT',
  'TRANSACTION_COORDINATOR',
  'BUYER',
  'SELLER',
]);

const AGENT_SIDE_ROLES = new Set(['LISTING_AGENT', 'BUYER_AGENT', 'TRANSACTION_COORDINATOR']);

export const isPrimaryParty = (role) => PRIMARY_PARTY_ROLES.has((role || '').toUpperCase());

// Phase 1: Transaction Documents files only (every folder, incl. disclosure).
// Returns null for anything that can't be summarised, which hides the item.
export const getSourceKey = (doc, role) => {
  if (!doc || !doc.documentId) return null;
  if ((doc.fileType || '').toLowerCase() !== 'application/pdf') return null;
  if ((doc.folder || '').toLowerCase() === 'inbox' && !AGENT_SIDE_ROLES.has((role || '').toUpperCase())) return null;
  return { sourceKind: 'FILE', sourceRef: doc.documentId, key: `FILE:${doc.documentId}` };
};

// ---------------------------------------------------------------------------
// Forms tab (Phase 2). Each Forms row maps to the same PDF the row's own View
// action opens; the backend re-fetches it through that same existing endpoint.
// Original forms are agent/TC only (as in the Forms tab); signed versions are
// open to Buyers/Sellers who are recipients — the backend enforces both.
// ---------------------------------------------------------------------------

const isLwFinalized = (doc) => doc?._clientSignedOnly === true
  || doc?._signing?.finalized === true
  || doc?._signing?.statusId === 3;

export const getFormSourceKey = (doc, role) => {
  if (!doc) return null;
  const agentSide = AGENT_SIDE_ROLES.has((role || '').toUpperCase());
  const name = doc.name || doc.title || doc.fileName || 'Form';
  const make = (sourceKind, sourceRef, extra = {}) => (sourceRef
    ? { sourceKind, sourceRef: String(sourceRef), key: `${sourceKind}:${sourceRef}`, displayName: name, ...extra }
    : null);

  // Lone Wolf
  if (doc._loneWolf && (doc._clientSignedOnly || doc._loneWolfSigningRow)) {
    return isLwFinalized(doc) ? make('LW_SIGNING', doc._signing?.signingId) : null;
  }
  if (doc._loneWolf) return agentSide ? make('LW_FORM', doc._versionId) : null;

  // Colorado (FormService)
  if (doc._formService) {
    if (doc._isEnvelopeRow) return make('ENVELOPE', doc.envelopeId);
    const instanceId = doc.id || doc.documentId;
    return agentSide && doc._packageId && instanceId ? make('CO_FORM', `${doc._packageId}|${instanceId}`) : null;
  }

  // CA DocuSign — client "shared with me" rows
  if (doc._sharedWith) {
    return make('ENVELOPE', doc.envelopeId, { envelopeDocumentId: doc._envelopeDocumentId });
  }
  // CA DocuSign — agent envelope rows (one per sent version)
  if (doc._isEnvelopeRow) return agentSide ? make('ENVELOPE', doc.envelopeId) : null;
  // CA DocuSign — original form / custom PDF (room document)
  const roomDocId = doc.id || doc.documentId;
  return agentSide && /^\d+$/.test(String(roomDocId || '')) ? make('DOCUSIGN_ROOM', roomDocId) : null;
};

// The row's state, per document kind. Stored with each generated version; when
// it changes (envelope signed, form edited, new version sent) the summary is
// flagged as having a newer version (FR-11). Used only for that prompt, never
// for access. Built ONLY from fields web and mobile derive identically, so a
// summary generated on one platform never shows a false prompt on the other —
// keep in sync with the mobile app's docAiSummaryRules.ts.
const isEnvelopeSigned = (doc) => {
  const s = String(doc?.status || '').toLowerCase();
  return !!(doc?.isSigned || doc?._envelopeAllCompleted
    || doc?._envelopeRecipientStatus === 'completed' || s === 'completed' || s === 'signed');
};

export const formSourceState = (doc, sourceKind) => {
  switch (sourceKind) {
    case 'ENVELOPE': return `env:${isEnvelopeSigned(doc) ? 'signed' : 'open'}`;
    case 'LW_SIGNING': return 'signing:final';
    case 'LW_FORM':
    case 'CO_FORM': return `mod:${doc?.createdDate || ''}`;
    case 'DOCUSIGN_ROOM':
      return `room:v${Array.isArray(doc?.versions) ? doc.versions.length : (doc?.envelopeId ? 1 : 0)}`;
    default: return '';
  }
};

// Only for older summaries without a content checksum. Once a version records one
// (contentTracked), transaction-service decides FR-11 by comparing checksums and
// row state is ignored — it includes dates, which change on a rename.
export const hasNewerFormVersion = (status, doc) => (
  !!status
  && status.status === 'READY'
  && !status.contentTracked
  && !!status.lastSourceState
  && status.lastSourceState !== formSourceState(doc, status.sourceKind)
);

// FR-11: the summary was built from the original and the document's envelope
// has since completed. envelopeStatus in the Documents list is already
// live-resolved by transaction-service, so this needs no extra request.
export const hasNewerSignedCopy = (status, doc) => (
  !!status
  && status.status === 'READY'
  && !!doc?.envelopeId
  && (doc.envelopeStatus || '').toUpperCase() === 'COMPLETED'
  && !(status.lastSourceKind === 'SIGNED' && status.lastEnvelopeId === doc.envelopeId)
);
